import { discountItemLineFn } from "~/class/billing/discountLine";
import { roundMoney } from "~/utils/money";
import { useFormatNumber } from "~/composables/useUtils";

/**
 * Price printed on a kitchen slip line, behind the "PrintShowPrice" system env.
 *
 * Port of legacy `thermalprinter.v2.js` `isShowPrice`, which appended
 * `"    " + (discountedprice || quantity * price)` to the item name. Three
 * legacy defects are fixed here:
 *   - toppings were folded into `e.price` AFTER the price string was built, so
 *     the printed number silently missed the topping money;
 *   - no decimal formatting (`50` instead of `50.00`);
 *   - the void slip kept the full-quantity amount after `quantity` had been
 *     replaced by `qtytovoid`.
 *
 * The printed value is the PER-UNIT net price — toppings in, discount off —
 * so it never disagrees with the quantity column next to it.
 */

/** Four spaces between the name and the price, the legacy separator. */
export const KITCHEN_PRICE_GAP = "    ";

/** Σ(topping price × topping quantity) — per piece, same formula as OrderItemHelper.sumTopping. */
function sumToppingPrice(item: any): number {
  if (!Array.isArray(item?.toppings)) return 0;
  return item.toppings.reduce(
    (acc: number, topping: any) => acc + (Number(topping?.price) || 0) * (Number(topping?.quantity) || 0),
    0
  );
}

/**
 * `(unit price + toppings) − per-unit discount`, rounded to 2 decimals.
 *
 * The discount comes from `discountItemLineFn` with `quantity: 1`: that yields
 * exactly the per-unit amount for both modes — mode 1 (baht) is stored per
 * piece already, mode 0 (percent) is a share of whatever amount it is handed.
 * No discount arithmetic is re-implemented here.
 */
export function kitchenUnitNetPrice(item: any): number {
  const gross = (Number(item?.price) || 0) + sumToppingPrice(item);

  const request = item?.discountrequest || item?.discountRequest || {};
  const discount = discountItemLineFn(gross, 1, Number(request.mode) || 0, Number(request.value) || 0);

  return roundMoney(gross - discount);
}

/** `ผัดไทย` → `ผัดไทย    @60.00`. Kept as a separate step so only the name changes. */
export function appendKitchenPrice(name: string, item: any): string {
  const money = useFormatNumber(kitchenUnitNetPrice(item), 2, true) as string;
  return `${name}${KITCHEN_PRICE_GAP}` + "@" + `${money}`;
}
